/**
 * Панель трассировки логики (ПЛ-26) и путей резерва (ПЛ-9) для выбранной работы: что показать на
 * диаграмме Ганта и в сетевом графике — предшественников, последователей или один путь резерва.
 */
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useState } from 'react';
import { Button, IconButton } from '../../ui/button';
import { Checkbox, Select } from '../../ui/form';
import { fmtDur, hpd, type View } from './model';
import { pathsTo, type Trace, type TraceDir, traceLogic } from './trace';

export type Focus =
  | { kind: 'trace'; trace: Trace }
  | { kind: 'path'; targetId: string; index: number; ids: string[] };

/** Набор работ, который показывать при текущем фокусе */
export function focusIds(v: View, f: Focus | null): Set<string> | null {
  if (!f) return null;
  const ids = new Set(v.activities.map((a) => a.id));
  // Работы уже нет (удалена, график загружен заново) — показываются все
  if (f.kind === 'path') {
    const left = f.ids.filter((id) => ids.has(id));
    return left.length ? new Set(left) : null;
  }
  return ids.has(f.trace.id) ? new Set(traceLogic(v, f.trace).keys()) : null;
}

const DIRS: Array<{ key: TraceDir; title: string }> = [
  { key: 'back', title: 'Предшественники' },
  { key: 'fwd', title: 'Последователи' },
  { key: 'both', title: 'Обе стороны' },
];

export function TraceBar({
  view,
  selectedId,
  focus,
  onFocus,
}: {
  view: View;
  selectedId: string | null;
  focus: Focus | null;
  onFocus: (f: Focus | null) => void;
}) {
  const [paths, setPaths] = useState<{ targetId: string; list: string[][] } | null>(null);
  const baseId = focus ? (focus.kind === 'trace' ? focus.trace.id : focus.targetId) : selectedId;
  const base = baseId ? view.activities.find((a) => a.id === baseId) : undefined;
  if (!base)
    // Работа, от которой строилась трассировка, удалена — фокус снимается
    return focus ? (
      <Button onClick={() => onFocus(null)} className="self-start max-md:h-11">
        Работа удалена — показать все работы
      </Button>
    ) : null;
  const byId = new Map(view.activities.map((a) => [a.id, a]));
  const h = hpd(view);
  const trace: Trace =
    focus?.kind === 'trace' ? focus.trace : { id: base.id, dir: 'both', drivingOnly: false, depth: 0 };
  const setTrace = (p: Partial<Trace>) => onFocus({ kind: 'trace', trace: { ...trace, id: base.id, ...p } });
  const counts = focus?.kind === 'trace' ? traceLogic(view, focus.trace) : null;
  let back = 0;
  let fwd = 0;
  for (const d of counts?.values() ?? []) {
    if (d < 0) back++;
    else if (d > 0) fwd++;
  }
  const shownPaths = paths?.targetId === base.id ? paths.list : null;
  const minTf = (ids: string[]) => {
    let m: number | null = null;
    for (const id of ids) {
      const tf = byId.get(id)?.dates?.tf;
      if (tf != null && (m == null || tf < m)) m = tf;
    }
    return m;
  };

  return (
    <section
      className="flex flex-col gap-2 rounded-md border border-line bg-surface-2 px-3 py-2.5"
      aria-label="Трассировка логики"
    >
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[14px]">
          Логика от {base.code} {base.name.length > 40 ? `${base.name.slice(0, 39)}…` : base.name}
        </b>
        {focus ? (
          <IconButton
            label="Показать все работы"
            onClick={() => onFocus(null)}
            className="ml-auto size-11 md:size-9"
          >
            <X className="size-4" />
          </IconButton>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <fieldset className="flex border-0 p-0 max-md:w-full" aria-label="Направление">
          {DIRS.map((d, i) => {
            const on = focus?.kind === 'trace' && focus.trace.dir === d.key;
            return (
              <button
                key={d.key}
                type="button"
                aria-pressed={on}
                onClick={() => setTrace({ dir: d.key })}
                className={clsx(
                  'min-h-11 border border-line px-3 text-[13px] max-md:flex-1 max-md:px-1 md:min-h-9',
                  i === 0 && 'rounded-l-md',
                  i === DIRS.length - 1 && 'rounded-r-md',
                  i > 0 && '-ml-px',
                  on ? 'bg-ink text-surface' : 'bg-surface hover:bg-surface-2',
                )}
              >
                {d.title}
              </button>
            );
          })}
        </fieldset>
        <div className="w-[150px]">
          <Select
            value={String(trace.depth)}
            onChange={(e) => setTrace({ depth: Number(e.target.value) })}
            className="max-md:h-11"
            aria-label="Сколько шагов по связям"
          >
            <option value="0">Все шаги</option>
            {[1, 2, 3, 5, 10].map((n) => (
              <option key={n} value={n}>
                Шагов: {n}
              </option>
            ))}
          </Select>
        </div>
        <Checkbox
          className="min-h-11 items-center md:min-h-0"
          label="Только ведущие связи"
          checked={trace.drivingOnly}
          onChange={(e) => setTrace({ drivingOnly: e.target.checked })}
        />
        <Button
          size="sm"
          onClick={() => setPaths({ targetId: base.id, list: pathsTo(view, base.id, 10) })}
          className="max-md:h-11"
        >
          Пути резерва к работе
        </Button>
      </div>
      {counts ? (
        <p className="text-[13px] text-ink-2" role="status">
          Показано работ: {counts.size} — предшественников {back}, последователей {fwd}.
        </p>
      ) : null}
      {shownPaths ? (
        shownPaths.length && (shownPaths.length > 1 || shownPaths[0]!.length > 1) ? (
          <ol className="flex flex-col gap-1 text-[13px]">
            {shownPaths.map((p, i) => {
              const on = focus?.kind === 'path' && focus.targetId === base.id && focus.index === i;
              const tf = minTf(p);
              return (
                <li key={p.join()}>
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      onFocus({
                        kind: 'path',
                        targetId: base.id,
                        index: i,
                        ids: p.includes(base.id) ? p : [...p, base.id],
                      })
                    }
                    className={clsx(
                      'min-h-11 w-full rounded-md px-2 py-1 text-left md:min-h-0',
                      on ? 'bg-ink text-surface' : 'hover:bg-surface',
                    )}
                  >
                    <b>Путь {i + 1}</b>
                    {i === 0 ? ' (ведущий)' : ''} · работ {p.length}
                    {tf != null ? ` · резерв ${fmtDur(tf, h)}` : ''}
                    <span className={clsx('block truncate', on ? 'text-surface' : 'text-ink-2')}>
                      {p.map((id) => byId.get(id)?.code ?? '').join(' → ')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="text-[13px] text-ink-2">
            У работы нет незавершённых предшественников с нулевым резервом связи — путей нет.
          </p>
        )
      ) : null}
    </section>
  );
}
